
///
/// EarthBrowser - Long Press Hook
///
/// Created: 2025-11-30
/// Copyright (c) 2025 Geodata Labs
///

import { useCallback, useRef } from "react"

interface UseLongPressOptions {
	onLongPress: () => void
	onClick?: () => void
	threshold?: number // ms to hold before triggering
}

export function useLongPress({ onLongPress, onClick, threshold = 2000 }: UseLongPressOptions) {
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
	const isLongPress = useRef(false)

	const start = useCallback(() => {
		isLongPress.current = false
		timerRef.current = setTimeout(() => {
			isLongPress.current = true
			// Haptic feedback on mobile if available
			navigator.vibrate?.(50)
			onLongPress()
		}, threshold)
	}, [onLongPress, threshold])

	const cancel = useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current)
		}
	}, [])

	const handleClick = useCallback(() => {
		// Only fire click if it wasn't a long press
		if (!isLongPress.current && onClick) {
			onClick()
		}
	}, [onClick])

	return {
		onMouseDown: start,
		onMouseUp: cancel,
		onMouseLeave: cancel,
		onTouchStart: start,
		onTouchEnd: cancel,
		onClick: handleClick,
	}
}

